// src/pages/tournamentDetails/components/TournamentClockCard.js
import React, { useEffect, useMemo, useState } from "react";

function clampNumber(value, min, max) {
  const n = Number(value);
  if (!Number.isFinite(n)) return min;
  return Math.min(Math.max(n, min), max);
}

function timestampToDate(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

function formatSavedAt(value) {
  const d = timestampToDate(value);
  if (!d) return "No checkpoint yet";
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit", second: "2-digit" });
}

function segmentLabel(item) {
  if (!item) return "—";
  if (item.type === "break") {
    return `Break — ${item.label || "Break"}`;
  }
  return `Level ${item.level}: ${Number(item.sb || 0).toLocaleString()}/${Number(
    item.bb || 0
  ).toLocaleString()}${item.ante ? ` (${Number(item.ante).toLocaleString()})` : ""}`;
}

export default function TournamentClockCard({
  clock,
  activeItem,
  timeRemainingMs,
  msToMMSS,
  isAdmin,
  isCompleted,
  showDetails,
  tournamentStatus,
  blinds,
  saving,
  onStartClock,
  onPauseClock,
  onResumeClock,
  onApplyManualClock,
  onPreviousSegment,
  onNextSegment,
  onRecoverClock,
}) {
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(Number(clock?.index || 0));
  const [minutesDraft, setMinutesDraft] = useState("0");
  const [secondsDraft, setSecondsDraft] = useState("00");

  const safeBlinds = useMemo(() => (Array.isArray(blinds) ? blinds : []), [blinds]);

  useEffect(() => {
    const index = clampNumber(clock?.index || 0, 0, Math.max(safeBlinds.length - 1, 0));
    const currentMs = Math.max(
      0,
      Number(
        timeRemainingMs ??
          clock?.remainingMs ??
          clock?.checkpointRemainingMs ??
          Number(safeBlinds[index]?.minutes || 0) * 60 * 1000
      )
    );

    setSelectedIndex(index);
    setMinutesDraft(String(Math.floor(currentMs / 60000)));
    setSecondsDraft(String(Math.floor((currentMs % 60000) / 1000)).padStart(2, "0"));
  }, [clock?.index, clock?.remainingMs, clock?.checkpointRemainingMs, timeRemainingMs, safeBlinds]);

  const selectedItem = safeBlinds[selectedIndex] || null;
  const canGoPrevious = selectedIndex > 0;
  const canGoNext = selectedIndex < safeBlinds.length - 1;

  const handleSelectedIndexChange = (event) => {
    const nextIndex = Number(event.target.value);
    const nextItem = safeBlinds[nextIndex];
    setSelectedIndex(nextIndex);
    setMinutesDraft(String(Math.max(0, Number(nextItem?.minutes || 0))));
    setSecondsDraft("00");
  };

  const getRemainingMs = () => {
    const minutes = clampNumber(minutesDraft, 0, 999);
    const seconds = clampNumber(secondsDraft, 0, 59);
    return Math.round((minutes * 60 + seconds) * 1000);
  };

  const apply = async (resume) => {
    if (!onApplyManualClock || !selectedItem) return;
    await onApplyManualClock({
      index: selectedIndex,
      remainingMs: getRemainingMs(),
      resume,
    });
  };

  return (
    <div className="bg-zinc-900 p-4 sm:p-5 rounded-xl border border-zinc-800 mb-8">
      <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
        <div>
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="text-xl font-semibold text-[#B87333]">Tournament Clock</h3>
            <span className="rounded-full border border-zinc-700 bg-zinc-950 px-2.5 py-1 text-xs font-semibold uppercase tracking-wide text-zinc-300">
              {clock?.status || "stopped"}
            </span>
          </div>

          {!showDetails ? (
            <p className="text-zinc-400 text-sm mt-2">
              Tournament has not started yet ({tournamentStatus}). Clock details will appear once running.
            </p>
          ) : null}
        </div>

        {isAdmin && !isCompleted ? (
          <div className="grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
            <button
              type="button"
              onClick={onStartClock}
              disabled={saving}
              className="px-4 py-3 rounded-lg bg-green-700 hover:bg-green-800 text-white font-semibold disabled:opacity-60"
            >
              Start Clock
            </button>

            <button
              type="button"
              onClick={onPauseClock}
              disabled={saving || clock?.status !== "running"}
              className="px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Pause
            </button>

            <button
              type="button"
              onClick={onResumeClock}
              disabled={saving || clock?.status !== "paused"}
              className="px-4 py-3 rounded-lg bg-[#B87333] hover:bg-[#A46230] text-zinc-950 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Resume
            </button>

            <button
              type="button"
              onClick={() => setAdvancedOpen((value) => !value)}
              disabled={saving}
              className="px-4 py-3 rounded-lg border border-zinc-700 bg-zinc-950 hover:bg-zinc-800 text-zinc-100 font-semibold disabled:opacity-60"
            >
              {advancedOpen ? "Hide Controls" : "Manual Controls"}
            </button>
          </div>
        ) : null}
      </div>

      {showDetails ? (
        <>
          {isCompleted ? (
            <p className="text-zinc-400 mb-3 mt-4">Tournament completed — clock stopped.</p>
          ) : null}

          <div className="mt-5 grid gap-3 sm:grid-cols-3">
            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
              <p className="text-zinc-500 text-sm">Current Segment</p>
              <p className="text-zinc-100 font-semibold mt-1">{segmentLabel(activeItem)}</p>
            </div>

            <div className="rounded-lg border border-[#B87333]/30 bg-[#B87333]/10 p-4">
              <p className="text-zinc-400 text-sm">Time Remaining</p>
              <p className="text-white text-3xl font-bold mt-1 tabular-nums">
                {(clock?.status === "running" || clock?.status === "paused") &&
                timeRemainingMs != null
                  ? msToMMSS(Math.max(0, timeRemainingMs))
                  : "—"}
              </p>
            </div>

            <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
              <p className="text-zinc-500 text-sm">Recovery Checkpoint</p>
              <p className="text-zinc-100 font-semibold mt-1">
                {formatSavedAt(clock?.checkpointAt)}
              </p>
              <p className="text-zinc-500 text-xs mt-1">
                {clock?.checkpointRemainingMs != null
                  ? `${msToMMSS(clock.checkpointRemainingMs)} saved`
                  : "Saved automatically while running"}
              </p>
            </div>
          </div>
        </>
      ) : null}

      {isAdmin && !isCompleted && advancedOpen ? (
        <div className="mt-5 rounded-xl border border-amber-500/30 bg-amber-500/5 p-4 sm:p-5">
          <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
            <div>
              <h4 className="text-lg font-semibold text-amber-200">Clock Recovery & Manual Control</h4>
              <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
                Select the correct segment and remaining time, then apply it paused or resume immediately.
                This is the emergency control when the tournament clock gets out of sync.
              </p>
            </div>

            <button
              type="button"
              onClick={onRecoverClock}
              disabled={saving || clock?.checkpointRemainingMs == null}
              className="px-4 py-3 rounded-lg border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-100 font-semibold disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Recover Last Checkpoint
            </button>
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[minmax(0,1fr)_220px]">
            <div>
              <label className="block text-zinc-300 text-sm font-semibold mb-2">Current Segment</label>
              <select
                value={selectedIndex}
                onChange={handleSelectedIndexChange}
                disabled={saving || safeBlinds.length === 0}
                className="w-full p-3.5 rounded-lg border border-zinc-700 bg-zinc-950 text-zinc-100"
              >
                {safeBlinds.map((item, index) => (
                  <option key={`${item?.type || "segment"}-${index}`} value={index}>
                    {index + 1}. {segmentLabel(item)} — {Number(item?.minutes || 0)} min
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-zinc-300 text-sm font-semibold mb-2">Time Remaining</label>
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
                <input
                  type="number"
                  min="0"
                  max="999"
                  inputMode="numeric"
                  value={minutesDraft}
                  onChange={(event) => setMinutesDraft(event.target.value)}
                  disabled={saving}
                  className="w-full p-3.5 rounded-lg border border-zinc-700 bg-zinc-950 text-center text-xl font-bold text-zinc-100"
                  aria-label="Minutes remaining"
                />
                <span className="text-zinc-400 text-xl font-bold">:</span>
                <input
                  type="number"
                  min="0"
                  max="59"
                  inputMode="numeric"
                  value={secondsDraft}
                  onChange={(event) => setSecondsDraft(event.target.value)}
                  disabled={saving}
                  className="w-full p-3.5 rounded-lg border border-zinc-700 bg-zinc-950 text-center text-xl font-bold text-zinc-100"
                  aria-label="Seconds remaining"
                />
              </div>
              <div className="mt-1 flex justify-between text-[11px] uppercase tracking-wide text-zinc-500">
                <span>Minutes</span>
                <span>Seconds</span>
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 sm:flex sm:flex-wrap gap-3">
            <button
              type="button"
              onClick={onPreviousSegment}
              disabled={saving || !canGoPrevious}
              className="px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold disabled:opacity-40"
            >
              ← Previous
            </button>

            <button
              type="button"
              onClick={onNextSegment}
              disabled={saving || !canGoNext}
              className="px-4 py-3 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-100 font-semibold disabled:opacity-40"
            >
              Next →
            </button>

            <button
              type="button"
              onClick={() => apply(false)}
              disabled={saving || !selectedItem}
              className="px-4 py-3 rounded-lg border border-zinc-600 bg-zinc-950 hover:bg-zinc-800 text-zinc-100 font-semibold disabled:opacity-40"
            >
              {saving ? "Saving..." : "Apply & Pause"}
            </button>

            <button
              type="button"
              onClick={() => apply(true)}
              disabled={saving || !selectedItem}
              className="px-4 py-3 rounded-lg bg-green-700 hover:bg-green-800 text-white font-semibold disabled:opacity-40"
            >
              {saving ? "Saving..." : "Apply & Resume"}
            </button>
          </div>

          <div className="mt-4 rounded-lg border border-zinc-800 bg-zinc-950 p-3 text-sm text-zinc-400">
            Selected: <span className="font-semibold text-zinc-100">{segmentLabel(selectedItem)}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
